import {useCallback, useEffect, useMemo, useState} from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {User} from '../../../../../api/accessControl/User.ts';
import {BackendApi} from '../../../../../api/api.ts';
import Shell from '../../../../../components/specific/Shell.tsx';
import {useDocumentDrawer, useOpenDocument} from '../../../../../views/DocumentDrawer';
import {useFilterStore} from '../../../../../contexts/FilterStore.tsx';
import {usePartnerClientScope} from '../../../../../hooks/usePartnerClientScope.ts';
import {ClientSubtitle} from '../../../../../components/specific/ClientSwitcher';
import {PartnerClientToolbar, getVisibleGoToItemIds} from '../../../../../components/specific/PartnerClientToolbar';
import {ArchivedDocumentsFilterBar} from './ArchivedDocumentsFilterBar.tsx';
import {ArchivedDocumentsTable} from './ArchivedDocumentsTable.tsx';
import {
    ArchivedDocument,
    ArchivedDocumentsFilters,
    ArchivedDocumentsSorting,
    DEFAULT_ARCHIVED_DOCUMENTS_SORTING,
} from './archivedDocuments.ts';

const ARCHIVED_DOCUMENTS_QUERY = 'partner-archived-documents';
const FILE_NAME_DEBOUNCE_MS = 300;
const NO_DOCUMENTS: ArchivedDocument[] = [];

export function PartnerArchivedDocumentsPage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const documentDrawer = useDocumentDrawer();
    const navigate = useNavigate();
    const location = useLocation();
    const {documentId: routeDocumentId} = useParams();
    const openDocumentId = useOpenDocument()?.documentId ?? null;
    const [handledRouteDocumentId, setHandledRouteDocumentId] = useState<string | null>(null);

    const {state: filterState, dispatch: filterDispatch} = useFilterStore();
    const dateFrom = filterState.common.dateRange.from;
    const dateTo = filterState.common.dateRange.to;
    const clientScope = usePartnerClientScope(currentUser);
    const clientId = clientScope.selectedClientId ?? '';
    const [fileNameInput, setFileNameInput] = useState('');
    const [fileName, setFileName] = useState('');
    const [sorting, setSorting] = useState<ArchivedDocumentsSorting>(DEFAULT_ARCHIVED_DOCUMENTS_SORTING);

    useEffect(() => {
        const timeout = setTimeout(() => setFileName(fileNameInput.trim()), FILE_NAME_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [fileNameInput]);

    const filters = useMemo<ArchivedDocumentsFilters>(
        () => ({dateFrom, dateTo, clientId, fileName}),
        [dateFrom, dateTo, clientId, fileName],
    );

    const documentsQuery = useQuery({
        queryKey: [ARCHIVED_DOCUMENTS_QUERY, filters, sorting],
        queryFn: async ({signal}) => {
            const response = await api.withAbortSignal(signal).Partner__listArchivedDocuments(
                filters.dateFrom || null,
                filters.dateTo || null,
                sorting.orderby,
                sorting.order,
                filters.clientId || null,
                filters.fileName,
            );
            return response.documents;
        },
        refetchOnWindowFocus: false,
        retry: false,
        placeholderData: keepPreviousData,
    });

    const documents = documentsQuery.data ?? NO_DOCUMENTS;

    const drawerDocuments = useMemo(
        () => documents.map((document) => ({
            id: document.id,
            businessId: document.business_id,
            fileName: document.file_name,
        })),
        [documents],
    );

    useEffect(() => {
        if (!routeDocumentId) {
            setHandledRouteDocumentId(null);
            return;
        }
        if (routeDocumentId === handledRouteDocumentId) {
            return;
        }
        if (routeDocumentId === openDocumentId) {
            setHandledRouteDocumentId(routeDocumentId);
            return;
        }
        if (documentsQuery.isPending || documentsQuery.isError) {
            return;
        }

        const document = documents.find((candidate) => candidate.id === routeDocumentId);
        if (document) {
            documentDrawer.open(document.business_id, document.id, {documentList: drawerDocuments});
        }
        setHandledRouteDocumentId(routeDocumentId);
    }, [
        documentsQuery.isPending,
        documentsQuery.isError,
        routeDocumentId,
        handledRouteDocumentId,
        openDocumentId,
        documents,
        drawerDocuments,
        documentDrawer,
    ]);

    useEffect(() => {
        if (routeDocumentId && routeDocumentId !== handledRouteDocumentId) {
            return;
        }

        const target = openDocumentId ? `/archived-documents/${openDocumentId}` : '/archived-documents';
        if (location.pathname === target) {
            return;
        }
        navigate(target, {replace: true});
    }, [openDocumentId, routeDocumentId, handledRouteDocumentId, location.pathname, navigate]);

    const handleDateRangeChange = useCallback(
        (from: string, to: string) =>
            filterDispatch({type: 'SET_FILTERS', payload: {common: {dateRange: {from, to}}}}),
        [filterDispatch],
    );

    const handleRowClick = useCallback(
        (documentId: string, businessId: string) =>
            documentDrawer.open(businessId, documentId, {documentList: drawerDocuments}),
        [documentDrawer, drawerDocuments],
    );

    return (
        <Shell
            currentUser={currentUser}
            title="Archief"
            subtitle={<ClientSubtitle prefix="Archief van" clientName={clientScope.selectedClient?.name ?? null} fallback="Documenten die je bewaart, doorzoekbaar voor Quinn"/>}
            titleActions={
                <PartnerClientToolbar
                    clients={clientScope.clients}
                    selectedClientId={clientScope.selectedClientId}
                    recentClientIds={clientScope.recentClientIds}
                    goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                    onSelectClient={clientScope.selectClient}
                />
            }
        >
            <div className="w-full">
                {documentsQuery.isError && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                        <p className="text-sm text-red-800">
                            Er is een fout opgetreden bij het ophalen van de gearchiveerde documenten. Probeer het later
                            opnieuw.
                        </p>
                    </div>
                )}

                <div className="rounded-lg bg-white">
                    <ArchivedDocumentsFilterBar
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        fileName={fileNameInput}
                        onDateRangeChange={handleDateRangeChange}
                        onFileNameChange={setFileNameInput}
                    />
                    {!documentsQuery.isError && (
                        <ArchivedDocumentsTable
                            documents={documents}
                            isLoading={documentsQuery.isPending || documentsQuery.isPlaceholderData}
                            sorting={sorting}
                            onSortingChange={setSorting}
                            onRowClick={handleRowClick}
                        />
                    )}
                </div>
            </div>
        </Shell>
    );
}
